import _ from "lodash";

import { CLIENT_ID_PARAM } from "./constants";

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
  // These options are needed to round to whole numbers if that's what you want.
  //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
});

export const formatAsCurrency = (value: number): string => {
  return currency.format(value);
};

/**
 * Sleep() returns a promise that resolves after a given number of milliseconds.
 * @param ms - The number of milliseconds to wait before resolving the promise.
 * @returns A promise that will resolve after the given amount of time.
 */
export const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const camelCaseToSentenceCase = (string: string): string => {
  return _(string)
    .chain()
    .replace(/([A-Z])/g, " $1")
    .toLower()
    .upperFirst()
    .value();
};

/**
 * It takes an array of strings and a separator, and returns a string that is the concatenation of the
 * strings in the array, separated by the separator with undefined values filtered out.
 * @param strings - an array of strings
 * @param separator - The separator to use when joining the strings. Defaults to a space.
 * @returns A function that takes two arguments, strings and separator, and returns a string.
 */
export const concatStrings = (
  strings: Array<string | null | undefined>,
  separator?: string
): string => {
  return _(strings)
    .chain()
    .filter((i) => Boolean(i))
    .join(separator ?? " ")
    .value();
};

export const isSandboxSubdomain = (): boolean => {
  if (typeof window === "undefined") return false; // Ensure it's running in a browser environment

  const hostname = window.location.hostname; // Get the current hostname
  const subdomains = hostname.split("."); // Split by dots

  return subdomains.some((sub) => sub.endsWith("sandbox"));
};

export const toggleSandboxSubdomain = (): void => {
  if (typeof window === "undefined") return; // Ensure it's running in a browser

  const { hostname, protocol, pathname, search, port } = window.location;

  let newHostname = hostname;

  if (hostname.includes("sandbox")) {
    // If 'sandbox' exists, remove it
    newHostname = hostname.replace(".sandbox", "").replace("sandbox.", "");
  } else {
    // If it's localhost, switch to sandbox.localhost
    if (hostname === "localhost") {
      newHostname = "sandbox.localhost";
    } else {
      // Otherwise, insert 'sandbox' after the first subdomain
      const parts = hostname.split(".");
      parts.splice(1, 0, "sandbox");
      newHostname = parts.join(".");
    }
  }

  // Preserve the port if it exists
  const portPart = port ? `:${port}` : "";
  const newUrl = `${protocol}//${newHostname}${portPart}${pathname}${search}`;
  window.location.href = newUrl; // Redirect to the new URL
};

/**
 * Utility function to append client ID to a URL path
 * @param path - The base path
 * @param clientId - The client ID to append
 * @returns The path with client ID parameter
 */
export const appendClientIdToPath = (
  path: string,
  clientId: string
): string => {
  if (!clientId || path.includes(`${CLIENT_ID_PARAM}=`)) {
    return path;
  }

  const hasParams = path.includes("?");
  const separator = hasParams ? "&" : "?";
  return `${path}${separator}${CLIENT_ID_PARAM}=${clientId}`;
};

// Sort policies based off refreshedAt, fallback to createdAt if refreshedAt is missing
export const sortPolicies = <
  T extends { refreshedAt?: string | null; createdAt?: string | null }
>(
  policies: T[]
): T[] => {
  const sortedPolicies = policies.sort((a, b) => {
    const aRefreshedAt = a.refreshedAt || a.createdAt;
    const bRefreshedAt = b.refreshedAt || b.createdAt;

    // Handle undefined/null values - sort them to the end
    if (!aRefreshedAt && !bRefreshedAt) return 0; // Both undefined, maintain order
    if (!aRefreshedAt) return 1; // a is undefined, sort to end
    if (!bRefreshedAt) return -1; // b is undefined, sort to end

    // Convert string dates to Date objects for proper chronological comparison
    const aDate = new Date(aRefreshedAt);
    const bDate = new Date(bRefreshedAt);

    // Check for invalid dates and sort them to the end
    if (isNaN(aDate.getTime()) && isNaN(bDate.getTime())) return 0; // Both invalid, maintain order
    if (isNaN(aDate.getTime())) return 1; // a is invalid, sort to end
    if (isNaN(bDate.getTime())) return -1; // b is invalid, sort to end

    if (aDate > bDate) return -1;
    if (aDate < bDate) return 1;
    return 0;
  });
  return sortedPolicies;
};

export const getErrorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error ? err.message || fallback : fallback;
